// Copyright: IQGeo Limited 2010-2022

import 'babel-polyfill';
import MywClass from 'myWorld/base/class';
import EventsMixin from 'myWorld/base/eventsMixin';
import platformVersionInfo from 'versionInfo';

const myw = {};
const buildVersionTag = document.head.querySelector('[name=myworld-project-build-version]');
myw.version = platformVersionInfo.myw_version;
myw.buildVersion = buildVersionTag && buildVersionTag.content;

myw.buildType = process.env.NODE_ENV;

//provide myw as global
global.myw = myw;

/**
 * Provides classical inheritance model. To be inherited by myWorld classes<br/>
 * Provides localisation methods
 * @name MywClass
 * @constructor
 * @extends LocalisationMixin
 */
myw.MywClass = myw.Class = MywClass;

myw.Events = EventsMixin;

/**
 * Object where feature model classes can be registered. <br/> Keyed on feature type name.
 * When a feature type is registered here, feature objects obtained from the database will inherit from the specified model.
 * @type {Object<string,class>}
 */
myw.featureModels = {};

/**
 * Object where datasource classes can be registered. <br/> Keyed on datasource type.
 * @type {Object}
 */
myw.datasourceTypes = {};

/**
 * Whether the current environment is the native app or not
 * @type {boolean}
 */
myw.isNativeApp = false;

/**
 * Object where modules can register a new tab in the Settings page of Configuration
 */
myw.configPagesSettingsTabs = {};

/**
 * Outputs line of text to browser console.
 * @param  {string}     module The module this trace line applies to.
 * @param  {integer}    level If this number is higher than or equal to trace level for the module then the line is written out.
 * @param  {...string}     msgs Message to show in console.
 */
myw.trace = (module, level, ...msgs) => {
    // ENH: If calculating msg in the caller proves to be costly then one option is to allow caller to pass a function/closure that
    // will only be evaluated if the trace level is appropriate.
    const moduleLogs = myw.tracing.logs[module];
    if (myw.isTracing(module, level) || moduleLogs) {
        msgs.unshift(`${module}:`);

        const isChrome = navigator.userAgent.indexOf('Chrome') !== -1;
        if (isChrome) {
            const error = new Error();
            Error.captureStackTrace(error, myw.trace);
            const stack = error.stack;
            const file = stack.split('\n')[1].split('/').splice(-1)[0].split(':')[0].split('?')[0];
            const line = stack.split('\n')[1].split('/').splice(-1)[0].split(':')[1];
            msgs.push('\t\t\t');
            msgs.push(`(${file}:${line})`);
        }

        if (level > 0) {
            const now = new Date();
            const msec = now.getMilliseconds();
            const pad = ['00', '0', ''][Math.floor(Math.log(msec) / Math.LN10)];
            msgs.unshift(
                `[${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}:${pad}${msec}]`
            );
        }
        const msg = msgs.join(' ');
        if (myw.isTracing(module, level)) console.log(msg);
        if (moduleLogs) {
            //limit to 200 messages to keep memory in check
            if (moduleLogs.length >= 200) moduleLogs.shift();
            moduleLogs.push(msg);
        }
    }
};

/**
 * Returns whether tracing is active for given module & level.
 * Useful for map related debugging.
 * @param  {string}     module The module this trace line applies to.
 * @param  {integer}    level If this number is higher than or equal to trace level for the module then the line is written out.
 * @return {boolean}
 */
myw.isTracing = (module, level) =>
    (module in myw.tracing.modules && myw.tracing.modules[module] >= level) ||
    ('all' in myw.tracing.modules && myw.tracing.modules['all'] >= level);

myw.tracing = {};

let traceSettings;
try {
    const traceSettingsStr = localStorage.getItem('mywTrace');
    traceSettings = traceSettingsStr && JSON.parse(traceSettingsStr);
} catch (e) {
    //ignore errors getting from local storage
}
myw.tracing.modules = traceSettings || {};
myw.tracing.logs = { application: [], sync: [] };

/**
 * Set the trace level for the specified module. Apart from the 'all' module, there is no explicitly defined list of modules. Specifying the 'all'
 * module allows a minimum trace level to be set across all modules. Suggested use during development
 * is to use the browser debugger console to make a call to myw.traceset to set the desired trace level.
 * @param {string}  module Module name
 * @param {integer} level  Required trace level
 */
myw.traceset = (module, level) => {
    myw.tracing.modules[module] = level;
    myw.tracing.logs[module] = [];
    try {
        localStorage.setItem('mywTrace', JSON.stringify(myw.tracing.modules));
    } catch (e) {
        //ignore as this isn't crucial
    }
};

export default myw;
